import { useStore } from "@/stores/useStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import Fuse from "fuse.js";
import { getEmployeeName } from "@/lib/employees";
import { Card } from "@/types/v2";
import { useFeatures } from "@/hooks/useFeatures";
import { firestoreToDateTime } from "@/lib/utils";
import { parse, startOfDay } from "date-fns";

interface UseCardFilteringOptions {
  applyLayoutFilter?: (cards: Card[]) => Card[];
}

export const useCardFiltering = (options?: UseCardFilteringOptions) => {
  const {
    cards,
    setDashboardSearchString,
    setDashboardTechnicianId,
    setDashboardServiceWriterId,
    dashboardSearchString,
    dashboardTechnicianId,
    dashboardServiceWriterId,
    dashboardDueInStart,
    setDashboardDueInStart,
    dashboardDueInEnd,
    setDashboardDueInEnd,
    dashboardDueOutStart,
    dashboardDueOutEnd,
    setDashboardDueOutStart,
    setDashboardDueOutEnd,
    dashboardApptStartStart,
    setDashboardApptStartStart,
    dashboardApptStartEnd,
    setDashboardApptStartEnd,
    dashboardApptEndStart,
    setDashboardApptEndStart,
    dashboardApptEndEnd,
    setDashboardApptEndEnd,
    employees,
    shop,
    dashboardTags,
    setDashboardTags,
    dashboardColors,
    setDashboardColors,
    dashboardContainer,
    setDashboardContainer,
  } = useStore();

  const [filteredCards, setFilteredCards] = useState(cards);
  const [search, setSearch] = useState(dashboardSearchString);
  const {
    features: { isTechnicianFilterEnabled, isServiceAdvisorFilterEnabled },
  } = useFeatures(shop);

  const [filteringServiceAdvisor, setFilteringServiceAdvisor] = useState(
    typeof dashboardServiceWriterId === "number"
      ? `${dashboardServiceWriterId}`
      : dashboardServiceWriterId
  );
  const [filteringTechnician, setFilteringTechnician] = useState(
    typeof dashboardTechnicianId === "number"
      ? `${dashboardTechnicianId}`
      : dashboardTechnicianId
  );

  const appliedFiltersCount = useMemo(() => {
    let filterCount = 0;
    if (
      ((typeof filteringServiceAdvisor === "string" &&
        filteringServiceAdvisor !== "all") ||
        (Array.isArray(filteringServiceAdvisor) &&
          filteringServiceAdvisor.length > 0)) &&
      isServiceAdvisorFilterEnabled
    ) {
      filterCount++;
    }
    if (
      (typeof filteringTechnician === "string" &&
        filteringTechnician !== "all") ||
      (Array.isArray(filteringTechnician) &&
        filteringTechnician.length > 0 &&
        isTechnicianFilterEnabled)
    ) {
      filterCount++;
    }
    if (dashboardDueInStart) {
      filterCount++;
    }
    if (dashboardDueInEnd) {
      filterCount++;
    }
    if (dashboardDueOutStart) {
      filterCount++;
    }
    if (dashboardDueOutEnd) {
      filterCount++;
    }
    if (dashboardApptStartStart) {
      filterCount++;
    }
    if (dashboardApptStartEnd) {
      filterCount++;
    }
    if (dashboardApptEndStart) {
      filterCount++;
    }
    if (dashboardApptEndEnd) {
      filterCount++;
    }
    if (dashboardTags && dashboardTags.length > 0) {
      filterCount += dashboardTags.length;
    }
    if (dashboardColors && dashboardColors.length > 0) {
      filterCount += dashboardColors.length;
    }
    if (dashboardContainer && dashboardContainer.length > 0) {
      filterCount += dashboardContainer.length;
    }
    return filterCount;
  }, [
    filteringServiceAdvisor,
    isServiceAdvisorFilterEnabled,
    filteringTechnician,
    isTechnicianFilterEnabled,
    dashboardDueInStart,
    dashboardDueInEnd,
    dashboardDueOutStart,
    dashboardDueOutEnd,
    dashboardApptStartStart,
    dashboardApptStartEnd,
    dashboardApptEndStart,
    dashboardApptEndEnd,
    dashboardTags,
    dashboardColors,
    dashboardContainer,
  ]);

  useEffect(() => {
    setFilteringTechnician(
      typeof dashboardTechnicianId === "number"
        ? `${dashboardTechnicianId}`
        : dashboardTechnicianId
    );
  }, [dashboardTechnicianId]);

  useEffect(() => {
    setFilteringServiceAdvisor(
      typeof dashboardServiceWriterId === "number"
        ? `${dashboardServiceWriterId}`
        : dashboardServiceWriterId
    );
  }, [dashboardServiceWriterId]);

  useEffect(() => {
    setSearch(dashboardSearchString);
  }, [dashboardSearchString]);

  const applyEmployeeFilter = (
    filterIds: number | string | string[] | undefined,
    cards: Card[],
    key: "technicianId" | "serviceWriterId"
  ) => {
    if (!filterIds) return cards;
    if (typeof filterIds === "string") {
      if (filterIds === "all") return cards;
      return cards.filter((card) => card[key] === filterIds);
    }
    if (typeof filterIds === "number") {
      if (filterIds === -1) return cards;
      return cards.filter((card) => card[key] === `${filterIds}`);
    }
    if (filterIds.length === 0) return cards;
    return cards.filter((card) =>
      card[key] ? filterIds.includes(card[key]) : false
    );
  };

  const applyDueFilter = (
    startDate: string | undefined | null,
    endDate: string | undefined | null,
    cards: Card[],
    type: "in" | "out" | "apptStart" | "apptEnd"
  ) => {
    return cards.filter((c) => {
      const getFilterDate = () => {
        switch (type) {
          case "in":
            return firestoreToDateTime(c.dropOffTime);
          case "out":
            return firestoreToDateTime(c.promisedTime);
          case "apptStart":
            return firestoreToDateTime(c.appointment?.startAt);
          case "apptEnd":
            return firestoreToDateTime(c.appointment?.endAt);
          default:
            return null;
        }
      };

      const filterDate = getFilterDate();
      if (startDate && endDate) {
        if (!filterDate) return false;
        return (
          startOfDay(filterDate) >=
            parse(startDate, "yyyy-MM-dd", new Date()) &&
          startOfDay(filterDate) <= parse(endDate, "yyyy-MM-dd", new Date())
        );
      }
      if (startDate) {
        if (!filterDate) return false;
        return (
          startOfDay(filterDate) >= parse(startDate, "yyyy-MM-dd", new Date())
        );
      }
      if (endDate) {
        if (!filterDate) return false;
        return (
          startOfDay(filterDate) <= parse(endDate, "yyyy-MM-dd", new Date())
        );
      }
      return true;
    });
  };

  const applyTagsFilter = (
    cards: Card[],
    tags: string[] | null | undefined
  ) => {
    if (!tags) return cards;
    if (tags.length === 0) return cards;
    return cards.filter((c) => c.tags?.some((t) => tags.includes(t)) ?? false);
  };

  const applyColorsFilter = (
    cards: Card[],
    colors: string[] | null | undefined
  ) => {
    if (!colors) return cards;
    if (colors.length === 0) return cards;
    return cards.filter((c) => colors.includes(c.color));
  };

  const applyContainerFilter = (
    cards: Card[],
    containerIds: string[] | null | undefined
  ) => {
    if (!containerIds || containerIds.length === 0) return cards;
    return cards.filter(
      (c) => c.currentContainer && containerIds.includes(c.currentContainer)
    );
  };

  const filterCards = useCallback(
    (cards: Card[]) => {
      let tmpCards = [...cards];

      // Apply layout filtering first (if provided)
      if (options?.applyLayoutFilter) {
        tmpCards = options.applyLayoutFilter(tmpCards);
      }

      if (isTechnicianFilterEnabled) {
        tmpCards = applyEmployeeFilter(
          dashboardTechnicianId,
          tmpCards,
          "technicianId"
        );
      }

      if (isServiceAdvisorFilterEnabled) {
        tmpCards = applyEmployeeFilter(
          dashboardServiceWriterId,
          tmpCards,
          "serviceWriterId"
        );
      }

      tmpCards = applyDueFilter(
        dashboardDueInStart,
        dashboardDueInEnd,
        tmpCards,
        "in"
      );

      tmpCards = applyDueFilter(
        dashboardDueOutStart,
        dashboardDueOutEnd,
        tmpCards,
        "out"
      );

      tmpCards = applyDueFilter(
        dashboardApptStartStart,
        dashboardApptStartEnd,
        tmpCards,
        "apptStart"
      );

      tmpCards = applyDueFilter(
        dashboardApptEndStart,
        dashboardApptEndEnd,
        tmpCards,
        "apptEnd"
      );

      tmpCards = applyTagsFilter(tmpCards, dashboardTags);

      tmpCards = applyColorsFilter(tmpCards, dashboardColors);

      tmpCards = applyContainerFilter(tmpCards, dashboardContainer);

      if (dashboardSearchString && dashboardSearchString !== "") {
        const fuse = new Fuse(tmpCards, {
          keys: [
            "id",
            "cardNumber",
            "keyTag",
            "primaryField",
            "secondaryField",
            "tertiaryField",
            "customer.firstName",
            "customer.lastName",
            "customer.name",
            "vehicle.make",
            "vehicle.year",
            "vehicle.model",
            "tags",
            {
              name: "technician",
              getFn: (card) =>
                getEmployeeName(card.technicianId, employees ?? []),
            },
            {
              name: "serviceWriter",
              getFn: (card) =>
                getEmployeeName(card.serviceWriterId, employees ?? []),
            },
          ],
          distance: 10,
          threshold: 0.1,
        });
        const results = fuse.search(dashboardSearchString);
        tmpCards = results.map((r) => r.item);
      }
      return tmpCards;
    },
    [
      options?.applyLayoutFilter,
      dashboardSearchString,
      dashboardServiceWriterId,
      dashboardTechnicianId,
      dashboardDueInStart,
      dashboardDueInEnd,
      dashboardDueOutStart,
      dashboardDueOutEnd,
      dashboardApptStartStart,
      dashboardApptStartEnd,
      dashboardApptEndStart,
      dashboardApptEndEnd,
      employees,
      isTechnicianFilterEnabled,
      isServiceAdvisorFilterEnabled,
      dashboardTags,
      dashboardColors,
      dashboardContainer,
    ]
  );

  useEffect(() => {
    setFilteredCards(filterCards(cards));
  }, [cards, filterCards]);

  const updateCardSearch = (search: string) => {
    setSearch(search);
    setDashboardSearchString(search);
  };

  const updateFilteringTechnician = (technicianId: string | string[]) => {
    setDashboardTechnicianId(technicianId);
  };

  const updateFilteringServiceAdvisor = (
    serviceAdvisorId: string | string[]
  ) => {
    setDashboardServiceWriterId(serviceAdvisorId);
  };

  const updateDueInStart = (value: string | undefined) => {
    setDashboardDueInStart(value);
  };

  const updateDueInEnd = (value: string | undefined) => {
    setDashboardDueInEnd(value);
  };

  const updateDueOutStart = (value: string | undefined) => {
    setDashboardDueOutStart(value);
  };

  const updateDueOutEnd = (value: string | undefined) => {
    setDashboardDueOutEnd(value);
  };

  const updateApptStartStart = (value: string | undefined) => {
    setDashboardApptStartStart(value);
  };

  const updateApptStartEnd = (value: string | undefined) => {
    setDashboardApptStartEnd(value);
  };

  const updateApptEndStart = (value: string | undefined) => {
    setDashboardApptEndStart(value);
  };

  const updateApptEndEnd = (value: string | undefined) => {
    setDashboardApptEndEnd(value);
  };

  const updateTags = (value: string[] | undefined) => {
    setDashboardTags(value);
  };

  const updateColors = (value: string[] | undefined) => {
    setDashboardColors(value);
  };

  const updateContainer = (value: string[] | undefined) => {
    setDashboardContainer(value);
  };

  return {
    filteredCards,
    search,
    updateCardSearch,
    filteringTechnician,
    updateFilteringTechnician,
    filteringServiceAdvisor,
    updateFilteringServiceAdvisor,
    dueInStart: dashboardDueInStart,
    dueInEnd: dashboardDueInEnd,
    updateDueInStart,
    updateDueInEnd,
    dueOutStart: dashboardDueOutStart,
    dueOutEnd: dashboardDueOutEnd,
    updateDueOutStart,
    updateDueOutEnd,
    apptStartStart: dashboardApptStartStart,
    apptStartEnd: dashboardApptStartEnd,
    updateApptStartStart,
    updateApptStartEnd,
    apptEndStart: dashboardApptEndStart,
    apptEndEnd: dashboardApptEndEnd,
    updateApptEndStart,
    updateApptEndEnd,
    filterTags: dashboardTags,
    updateTags,
    appliedFiltersCount,
    filterColors: dashboardColors,
    updateColors,
    filterContainer: dashboardContainer,
    updateContainer,
  };
};
